import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
} from 'firebase/firestore';

import { db } from '../../utils/firebaseConfig';
import type { Announcement, CreateAnnouncementPayload } from '../../types/domain';

const ANNOUNCEMENTS_COLLECTION = 'announcements';
const USERS_COLLECTION = 'users';

const toISOString = (value: unknown): string => {
  if (value instanceof Timestamp) {
    return value.toDate().toISOString();
  }

  if (value && typeof value === 'object' && 'toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }

  if (typeof value === 'string') {
    return value;
  }

  return '';
};

const toSafeString = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

export const getAnnouncements = async (userId?: string): Promise<Announcement[]> => {
  const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
  const announcementsQuery = query(announcementsRef, orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(announcementsQuery);

  const readByAnnouncementId: Record<string, string> = {};
  if (userId) {
    const readsRef = collection(db, USERS_COLLECTION, userId, 'announcementReads');
    const readsSnapshot = await getDocs(readsRef);
    readsSnapshot.forEach((snapshot) => {
      const readAt = toISOString(snapshot.data().readAt);
      if (readAt) {
        readByAnnouncementId[snapshot.id] = readAt;
      }
    });
  }

  const nowTimestamp = Date.now();

  const announcements = querySnapshot.docs.map((snapshot) => {
    const data = snapshot.data() as Record<string, unknown>;
    const expiresAt = toISOString(data.expiresAt);
    const readAt = readByAnnouncementId[snapshot.id] || '';

    return {
      id: snapshot.id,
      title: toSafeString(data.title),
      message: toSafeString(data.message),
      createdAt: toISOString(data.createdAt),
      createdBy: toSafeString(data.createdBy),
      expiresAt,
      isRead: Boolean(readAt),
      readAt,
    };
  });

  return announcements.filter((announcement) => {
    if (!announcement.expiresAt) {
      return true;
    }

    const expiryTimestamp = new Date(announcement.expiresAt).getTime();
    return Number.isFinite(expiryTimestamp) && expiryTimestamp > nowTimestamp;
  });
};

export const createAnnouncement = async ({
  title,
  message,
  createdBy,
  expiresAt,
}: CreateAnnouncementPayload): Promise<void> => {
  await addDoc(collection(db, ANNOUNCEMENTS_COLLECTION), {
    title,
    message,
    createdBy,
    createdAt: serverTimestamp(),
    expiresAt: expiresAt ? Timestamp.fromDate(new Date(expiresAt)) : null,
  });
};

export const removeAnnouncement = async (announcementId: Announcement['id']): Promise<void> => {
  await deleteDoc(doc(db, ANNOUNCEMENTS_COLLECTION, announcementId));
};

export const markAnnouncementRead = async (
  userId: string,
  announcementId: Announcement['id']
): Promise<void> => {
  const readDocRef = doc(db, USERS_COLLECTION, userId, 'announcementReads', announcementId);
  await setDoc(readDocRef, {
    readAt: serverTimestamp(),
  }, { merge: true });
};

export const markAnnouncementUnread = async (
  userId: string,
  announcementId: Announcement['id']
): Promise<void> => {
  const readDocRef = doc(db, USERS_COLLECTION, userId, 'announcementReads', announcementId);
  const readSnapshot = await getDoc(readDocRef);

  if (!readSnapshot.exists()) {
    return;
  }

  await deleteDoc(readDocRef);
};
